import React, { useState } from "react";
import styles from "./Privacy.module.css";
import diffusionIconLight from "../../assets/diffusion_light.svg";
import diffusionIconDark from "../../assets/diffusion_dark.svg";
import menuIconLight from "../../assets/menu_light.svg";
import menuIconDark from "../../assets/menu_dark.svg";
import closeIconLight from "../../assets/close_light.svg";
import closeIconDark from "../../assets/close_dark.svg";
import lightIconActiveLightTheme from "../../assets/light_icon_active_light.svg";
import darkIconActiveDarkTheme from "../../assets/dark_icon_active_dark.svg";
import { useTheme } from "../../App";

interface HomeProps {
  onNavigateToHome?: () => void;
  onNavigateToSignUp: () => void;
  onNavigateToLogin: () => void;
}

const Privacy: React.FC<HomeProps> = ({
  onNavigateToHome,
  onNavigateToSignUp,
  onNavigateToLogin,
}) => {
  const { theme, setTheme } = useTheme();
  const getActualTheme = (): "light" | "dark" => {
    if (theme === "system") {
      return window.matchMedia("(prefers-color-scheme: dark)").matches
        ? "dark"
        : "light";
    }
    return theme as "light" | "dark";
  };

  const actualTheme = getActualTheme();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [showFooterBorder, setShowFooterBorder] = useState(true);

  const diffusionIcon =
    actualTheme === "dark" ? diffusionIconDark : diffusionIconLight;
  const menuIcon = actualTheme === "dark" ? menuIconDark : menuIconLight;
  const closeIcon = actualTheme === "dark" ? closeIconDark : closeIconLight;
  const themeIcon =
    actualTheme === "light"
      ? lightIconActiveLightTheme
      : darkIconActiveDarkTheme;
  const handleThemeToggle = () => {
    document.documentElement.classList.add("theme-transitioning");
    setTheme(theme === "light" ? "dark" : "light");
    setTimeout(() => {
      document.documentElement.classList.remove("theme-transitioning");
    }, 0);
  };

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  // Check if scrolled to bottom for footer border
  React.useEffect(() => {
    const handleScroll = () => {
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      const scrollTop = window.scrollY;

      if (windowHeight + scrollTop >= documentHeight - 10) {
        setShowFooterBorder(false);
      } else {
        setShowFooterBorder(true);
      }
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll();

    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Listen for scroll to show/hide border
  React.useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 0) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Prevent scrolling when menu is open
  React.useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
  }, [isMenuOpen]);

  return (
    <div className={styles.container}>
      {/* Navbar */}
      <nav
        className={`${styles.navbar} ${
          isScrolled ? styles.navbarScrolled : ""
        }`}
      >
        <div className={styles.navContent}>
          <button
            className={styles.logoButton}
            onClick={() => {
              window.scrollTo(0, 0);
              if (onNavigateToHome) {
                window.history.pushState({}, "", "/home");
                onNavigateToHome();
              } else {
                window.location.href = "/";
              }
            }}
          >
            <img src={diffusionIcon} alt="Logo" className={styles.logoIcon} />
          </button>

          <div className={styles.desktopMenu}>
            <a
              href="#features"
              className={styles.navLink}
              onClick={(e) => {
                e.preventDefault();
                window.history.pushState({}, "", "/home");
                if (onNavigateToHome) onNavigateToHome();
                setTimeout(() => {
                  document
                    .getElementById("features")
                    ?.scrollIntoView({ behavior: "smooth" });
                }, 100);
              }}
            >
              Features
            </a>
            <a
              href="#pricing"
              className={styles.navLink}
              onClick={(e) => {
                e.preventDefault();
                window.history.pushState({}, "", "/home");
                if (onNavigateToHome) onNavigateToHome();
                setTimeout(() => {
                  document
                    .getElementById("pricing")
                    ?.scrollIntoView({ behavior: "smooth" });
                }, 100);
              }}
            >
              Pricing
            </a>
            <a
              href="#faqs"
              className={styles.navLink}
              onClick={(e) => {
                e.preventDefault();
                window.history.pushState({}, "", "/home");
                if (onNavigateToHome) onNavigateToHome();
                setTimeout(() => {
                  document
                    .getElementById("faqs")
                    ?.scrollIntoView({ behavior: "smooth" });
                }, 100);
              }}
            >
              FAQ's
            </a>
            <a
              href="/guide"
              className={styles.footerLink}
              onClick={(e) => {
                e.preventDefault();
                window.history.pushState({}, "", "/guide");
                window.dispatchEvent(new Event("popstate"));
              }}
            >
              Blog
            </a>
          </div>

          <div className={styles.rightButtons}>
            <div
              className={`${styles.authButtons} ${
                isMenuOpen ? styles.hiddenAuthButtons : ""
              }`}
            >
              <button
                onClick={() => {
                  window.history.pushState({}, "", "/login");
                  onNavigateToLogin();
                }}
                className={styles.loginButton}
              >
                Log in
              </button>
              <button
                onClick={() => {
                  window.history.pushState({}, "", "/signup");
                  onNavigateToSignUp();
                }}
                className={styles.getStartedButton}
              >
                Get started
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile dropdown menu */}
      {isMenuOpen && (
        <div className={styles.mobileMenuDropdown}>
          <div className={styles.mobileMenuContent}>
            <a
              href="#features"
              className={styles.mobileMenuItem}
              onClick={(e) => {
                e.preventDefault();
                setIsMenuOpen(false);
                window.history.pushState({}, "", "/home");
                if (onNavigateToHome) onNavigateToHome();
                setTimeout(() => {
                  document
                    .getElementById("features")
                    ?.scrollIntoView({ behavior: "smooth" });
                }, 100);
              }}
            >
              Features
            </a>
            <div className={styles.menuDivider}></div>
            <a
              href="#pricing"
              className={styles.mobileMenuItem}
              onClick={(e) => {
                e.preventDefault();
                setIsMenuOpen(false);
                window.history.pushState({}, "", "/home");
                if (onNavigateToHome) onNavigateToHome();
                setTimeout(() => {
                  document
                    .getElementById("pricing")
                    ?.scrollIntoView({ behavior: "smooth" });
                }, 100);
              }}
            >
              Pricing
            </a>
            <div className={styles.menuDivider}></div>
            <a
              href="#faqs"
              className={styles.mobileMenuItem}
              onClick={(e) => {
                e.preventDefault();
                setIsMenuOpen(false);
                window.history.pushState({}, "", "/home");
                if (onNavigateToHome) onNavigateToHome();
                setTimeout(() => {
                  document
                    .getElementById("faqs")
                    ?.scrollIntoView({ behavior: "smooth" });
                }, 100);
              }}
            >
              FAQ's
            </a>
            <div className={styles.menuDivider}></div>
            <a
              href="/guide"
              className={styles.footerLink}
              onClick={(e) => {
                e.preventDefault();
                window.history.pushState({}, "", "/guide");
                window.dispatchEvent(new Event("popstate"));
              }}
            >
              Blog
            </a>
          </div>

          <div className={styles.mobileMenuButtons}>
            <button
              onClick={() => {
                window.history.pushState({}, "", "/signup");
                onNavigateToSignUp();
              }}
              className={styles.mobileGetStartedButton}
            >
              Get started
            </button>
            <button
              onClick={() => {
                window.history.pushState({}, "", "/login");
                onNavigateToLogin();
              }}
              className={styles.mobileLoginButton}
            >
              Log in
            </button>
          </div>
        </div>
      )}

      <div className={styles.pageContent}>
        <div className={styles.heroContent}>
          <h1 className={styles.heroTitle}>Privacy Policy</h1>
          <p className={styles.text}>
            <span className={styles.bold}>Effective date:</span> January 14 2026
          </p>
          {/* <p className={styles.text}>
            <span className={styles.bold}>Last updated:</span> 01 November 2025
          </p> */}
          <p className={styles.para}>
            This <span className={styles.bold}>Privacy Policy</span> explains
            how <span className={styles.bold}>Noah ("we," "us," "our") </span>{" "}
            collects, uses, and protects your personal information when you use
            our Service.
          </p>
          <div className={styles.divider}></div>
          <h2 className={styles.subheader}>1. Information We Collect</h2>
          <p className={styles.para}>
            We collect the following types of data:
            <ul className={styles.list2}>
              <li>Full name, email address, password, and profile image.</li>
              <li>
                Chat prompts, model history, and interaction data within the
                app.
              </li>
              <li>
                Technical data like IP address, browser type, and device
                information.
              </li>
              <li>
                Purchase details processed by{" "}
                <span className={styles.bold}>Stripe</span>.
              </li>
              <li>
                We <span className={styles.bold}>do not</span> store or handle
                credit card numbers directly.
              </li>
              <li>
                Voluntary feedback, bug reports, or feature requests you share
                with us.
              </li>
            </ul>
          </p>
          <div className={styles.divider2}></div>
          <h2 className={styles.subheader}>2. How We Use Your Data</h2>
          <p className={styles.para}>
            We use your data to:
            <ul>
              <li>Operate and maintain Noah.</li>
              <li>Manage accounts, credits, and payments.</li>
              <li>Provide customer support.</li>
              <li>Improve AI model accuracy and platform reliability.</li>
              <li>Send updates or notices (only when necessary).</li>
            </ul>
            Aggregated and anonymized usage data may be used for analytics and
            product improvement.
          </p>
          <div className={styles.divider}></div>
          <h2 className={styles.subheader}>3. AI Training and Feedback Use</h2>
          <p className={styles.para}>
            Your <span className={styles.bold}>feedback</span> and{" "}
            <span className={styles.bold}>anonymized interaction data</span> may
            be used internally to train or enhance our AI systems. We{" "}
            <span className={styles.bold}>do not</span> use your{" "}
            <span className={styles.bold}>private model data</span> or
            identifiable content to train our AI models.
          </p>
          <div className={styles.divider}></div>
          <h2 className={styles.subheader}>4. Data Sharing</h2>
          <p className={styles.para}>
            We only share information with:
            <ul>
              <li>
                <span className={styles.bold}>Service providers</span> (e.g.,
                Stripe, hosting, analytics) under strict confidentiality.
              </li>
              <li>
                <span className={styles.bold}>Authorities</span>, if required by
                law, court order, or to prevent fraud or abuse.
              </li>
            </ul>
            We <span className={styles.bold}>never sell</span> personal data.
          </p>
          <div className={styles.divider}></div>
          <h2 className={styles.subheader}>5. Data Retention and Deletion</h2>
          <p className={styles.para}>
            You can delete your account at any time through{" "}
            <span className={styles.bold}>Settings</span>. When you do:
          </p>

          <ul className={styles.list2}>
            <li>
              Your personal information and models are permanently deleted.
            </li>
            <li>
              Billing records may be retained as required by financial laws.
            </li>
          </ul>

          <div className={styles.divider2}></div>
          <h2 className={styles.subheader}>6. Data Security</h2>
          <p className={styles.para}>
            We use encryption, secure servers, and access controls to safeguard
            your data. While we take strong precautions, no system is 100%
            secure - use Noah at your own risk.
          </p>
          <div className={styles.divider}></div>
          <h2 className={styles.subheader}>7. Children's Privacy</h2>
          <p className={styles.para}>
            Noah is not intended for users under 13 years old. We do not
            knowingly collect information from minors.
          </p>
          <div className={styles.divider}></div>
          <h2 className={styles.subheader}>8. Your Rights</h2>
          <p className={styles.para}>
            Depending on your region (e.g., EU, California), you may:
            <ul>
              <li>Access or correct your data.</li>
              <li>Request deletion of your account.</li>
              <li>Opt out of promotional communications (if applicable).</li>
            </ul>
            Contact us at{" "}
            <a
              href="https://gmail.com"
              target="_blank"
              rel="noopener noreferrer"
              className={styles.link}
            >
              support@noahcad.com
            </a>{" "}
            to make a data request.
          </p>
          <div className={styles.divider}></div>
          <h2 className={styles.subheader}>9. Updates to This Policy</h2>
          <p className={styles.para}>
            We may revise this Privacy Policy periodically. Updated versions
            will include a new "Last Updated" date and take effect immediately
            upon posting.
          </p>
          <div className={styles.divider}></div>
          <h2 className={styles.subheader}>10. Contact</h2>
          <p className={styles.para}>
            If you have privacy questions, please contact:{" "}
            <a
              href="https://gmail.com"
              target="_blank"
              rel="noopener noreferrer"
              className={styles.link}
            >
              support@noahcad.com
            </a>
            .
          </p>
        </div>
      </div>

      {/* Footer */}
      <footer
        className={`${styles.footer} ${
          showFooterBorder ? styles.footerBordered : ""
        }`}
      >
        <div className={styles.footerContent}>
          <div
            className={styles.footerLogo}
            onClick={() => {
              window.scrollTo(0, 0);
              if (onNavigateToHome) {
                window.history.pushState({}, "", "/home");
                onNavigateToHome();
              } else {
                window.location.href = "/";
              }
            }}
          >
            <img src={diffusionIcon} alt="Logo" className={styles.logoIcon} />
          </div>

          <div className={styles.footerGrid}>
            <div className={styles.footerColumn}>
              <h3 className={styles.footerColumnTitle}>Product</h3>
              <a
                href="#features"
                className={styles.footerLink}
                onClick={(e) => {
                  e.preventDefault();
                  window.history.pushState({}, "", "/home");
                  if (onNavigateToHome) onNavigateToHome();
                  setTimeout(() => {
                    document
                      .getElementById("features")
                      ?.scrollIntoView({ behavior: "smooth" });
                  }, 100);
                }}
              >
                Features
              </a>
              <a
                href="#pricing"
                className={styles.footerLink}
                onClick={(e) => {
                  e.preventDefault();
                  window.history.pushState({}, "", "/home");
                  if (onNavigateToHome) onNavigateToHome();
                  setTimeout(() => {
                    document
                      .getElementById("pricing")
                      ?.scrollIntoView({ behavior: "smooth" });
                  }, 100);
                }}
              >
                Pricing
              </a>
              <a
                href="#faqs"
                className={styles.footerLink}
                onClick={(e) => {
                  e.preventDefault();
                  window.history.pushState({}, "", "/home");
                  if (onNavigateToHome) onNavigateToHome();
                  setTimeout(() => {
                    document
                      .getElementById("faqs")
                      ?.scrollIntoView({ behavior: "smooth" });
                  }, 100);
                }}
              >
                FAQ's
              </a>
            </div>

            <div className={styles.footerColumn}>
              <h3 className={styles.footerColumnTitle}>Company</h3>
              <a
                href="/guide"
                className={styles.footerLink}
                onClick={(e) => {
                  e.preventDefault();
                  window.history.pushState({}, "", "/guide");
                  window.dispatchEvent(new Event("popstate"));
                }}
              >
                Blog
              </a>
            </div>

            <div className={styles.footerColumn}>
              <h3 className={styles.footerColumnTitle}>Resources</h3>
              <a
                href="/guide"
                className={styles.footerLink}
                onClick={(e) => {
                  e.preventDefault();
                  window.history.pushState({}, "", "/guide");
                  window.dispatchEvent(new Event("popstate"));
                }}
              >
                Modeling guide
              </a>
              <a
                href="/terms"
                className={styles.footerLink}
                onClick={(e) => {
                  e.preventDefault();
                  window.history.pushState({}, "", "/terms");
                  window.dispatchEvent(new Event("popstate"));
                }}
              >
                Terms of service
              </a>
              <a
                href="/privacy"
                className={styles.footerLink}
                onClick={(e) => {
                  e.preventDefault();
                  window.history.pushState({}, "", "/privacy");
                  window.dispatchEvent(new Event("popstate"));
                }}
              >
                Privacy policy
              </a>
            </div>
          </div>

          <div className={styles.footerBottom}>
            <div className={styles.footerSocial}>
              {/* <button
                className={styles.themeButton}
                onClick={handleThemeToggle}
              >
                <img
                  src={themeIcon}
                  alt="Toggle theme"
                  className={styles.themeIcon}
                />
              </button> */}
              <a
                href="https://x.com/cadwithnoah"
                target="_blank"
                rel="noopener noreferrer"
                className={styles.socialLink}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                </svg>
              </a>
              <a
                href="https://bsky.app/profile/noahcad.com"
                target="_blank"
                rel="noopener noreferrer"
                className={styles.socialLink}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M12 10.8c-1.087-2.114-4.046-6.053-6.798-7.995C2.566.944 1.561 1.266.902 1.565.139 1.908 0 3.08 0 3.768c0 .69.378 5.65.624 6.479.815 2.736 3.713 3.66 6.383 3.364.136-.02.275-.039.415-.056-.138.022-.276.04-.415.056-3.912.58-7.387 2.005-2.83 7.078 5.013 5.19 6.87-1.113 7.823-4.308.953 3.195 2.05 9.271 7.733 4.308 4.267-4.308 1.172-6.498-2.74-7.078a8.741 8.741 0 0 1-.415-.056c.14.017.279.036.415.056 2.67.297 5.568-.628 6.383-3.364.246-.828.624-5.79.624-6.478 0-.69-.139-1.861-.902-2.206-.659-.298-1.664-.62-4.3 1.24C16.046 4.748 13.087 8.687 12 10.8Z" />
                </svg>
              </a>
              <a
                href="https://www.threads.net/@cadwithnoah"
                target="_blank"
                rel="noopener noreferrer"
                className={styles.socialLink}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 192 192"
                  fill="currentColor"
                >
                  <path d="M141.537 88.9883C140.71 88.5919 139.87 88.2104 139.019 87.8451C137.537 60.5382 122.616 44.905 97.5619 44.745C97.4484 44.7443 97.3355 44.7443 97.222 44.7443C82.2364 44.7443 69.7731 51.1409 62.102 62.7807L75.881 72.2328C81.6116 63.5383 90.6052 61.6848 97.2286 61.6848C97.3051 61.6848 97.3819 61.6848 97.4576 61.6855C105.707 61.7381 111.932 64.1366 115.961 68.814C118.893 72.2193 120.854 76.925 121.825 82.8638C114.511 81.6207 106.601 81.2385 98.145 81.7233C74.3247 83.0954 59.0111 96.9879 60.0396 116.292C60.5615 126.084 65.4397 134.508 73.775 140.011C80.8224 144.663 89.899 146.938 99.3323 146.423C111.79 145.74 121.563 140.987 128.381 132.296C133.559 125.696 136.834 117.143 138.28 106.366C144.217 109.949 148.617 114.664 151.047 120.332C155.179 129.967 155.42 145.8 142.501 158.708C131.182 170.016 117.576 174.908 97.0135 175.059C74.2042 174.89 56.9538 167.575 45.7381 153.317C35.2355 139.966 29.8077 120.682 29.6052 96C29.8077 71.3178 35.2355 52.0336 45.7381 38.6827C56.9538 24.4249 74.2039 17.11 97.0132 16.9405C119.988 17.1113 137.539 24.4614 149.184 38.788C154.894 45.8136 159.199 54.6488 162.037 64.9503L178.184 60.6422C174.744 47.9622 169.331 37.0357 161.965 27.974C147.036 9.60668 125.202 0.195148 97.0695 0H96.9569C68.8816 0.19447 47.2921 9.6418 32.7883 28.0793C19.8819 44.4864 13.2244 67.3157 13.0007 95.9325L13 96L13.0007 96.0675C13.2244 124.684 19.8819 147.514 32.7883 163.921C47.2921 182.358 68.8816 191.806 96.9569 192H97.0695C122.03 191.827 139.624 185.292 154.118 170.811C173.081 151.866 172.51 128.119 166.26 113.541C161.776 103.087 153.227 94.5962 141.537 88.9883ZM98.4405 129.507C88.0005 130.095 77.1544 125.409 76.6196 115.372C76.2232 107.93 81.9158 99.626 99.0812 98.6368C101.047 98.5234 102.976 98.468 104.871 98.468C111.106 98.468 116.939 99.0737 122.242 100.233C120.264 124.935 108.662 128.946 98.4405 129.507Z" />
                </svg>
              </a>
              <a
                href="https://www.youtube.com/@noahcad"
                target="_blank"
                rel="noopener noreferrer"
                className={styles.socialLink}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
                </svg>
              </a>
              <a
                href="https://www.tiktok.com/@cadwithnoah"
                target="_blank"
                rel="noopener noreferrer"
                className={styles.socialLink}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z" />
                </svg>
              </a>
              <a
                href="https://www.reddit.com/r/NoahCAD/"
                target="_blank"
                rel="noopener noreferrer"
                className={styles.socialLink}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M12 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0zm5.01 4.744c.688 0 1.25.561 1.25 1.249a1.25 1.25 0 0 1-2.498.056l-2.597-.547-.8 3.747c1.824.07 3.48.632 4.674 1.488.308-.309.73-.491 1.207-.491.968 0 1.754.786 1.754 1.754 0 .716-.435 1.333-1.01 1.614a3.111 3.111 0 0 1 .042.52c0 2.694-3.13 4.87-7.004 4.87-3.874 0-7.004-2.176-7.004-4.87 0-.183.015-.366.043-.534A1.748 1.748 0 0 1 4.028 12c0-.968.786-1.754 1.754-1.754.463 0 .898.196 1.207.49 1.207-.883 2.878-1.43 4.744-1.487l.885-4.182a.342.342 0 0 1 .14-.197.35.35 0 0 1 .238-.042l2.906.617a1.214 1.214 0 0 1 1.108-.701zM9.25 12C8.561 12 8 12.562 8 13.25c0 .687.561 1.248 1.25 1.248.687 0 1.248-.561 1.248-1.249 0-.688-.561-1.249-1.249-1.249zm5.5 0c-.687 0-1.248.561-1.248 1.25 0 .687.561 1.248 1.249 1.248.688 0 1.249-.561 1.249-1.249 0-.687-.562-1.249-1.25-1.249zm-5.466 3.99a.327.327 0 0 0-.231.094.33.33 0 0 0 0 .463c.842.842 2.484.913 2.961.913.477 0 2.105-.056 2.961-.913a.361.361 0 0 0 .029-.463.33.33 0 0 0-.464 0c-.547.533-1.684.73-2.512.73-.828 0-1.979-.196-2.512-.73a.326.326 0 0 0-.232-.095z" />
                </svg>
              </a>
              <a
                href="https://www.linkedin.com/company/noahcad"
                target="_blank"
                rel="noopener noreferrer"
                className={styles.socialLink}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                </svg>
              </a>
              <a
                href="https://www.instagram.com/cadwithnoah/?igsh=MWkwbGd2bnNmZWIzNg%3D%3D&utm_source=qr"
                target="_blank"
                rel="noopener noreferrer"
                className={styles.socialLink}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                </svg>
              </a>
              <a
                href="https://discord.gg/mYG5ZDfmP7"
                target="_blank"
                rel="noopener noreferrer"
                className={styles.socialLink}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z" />
                </svg>
              </a>
            </div>

            <div className={styles.footerCopyright}>
              <span className={styles.copyright}>©</span> Noah 2026. All rights
              reserved.
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Privacy;
